import React, { useState, useEffect, useMemo, useRef } from "react";
import "./Reports.scss";
import ConsentSummary from "./ConsentSummary";
import { useCountryList } from "../../services/okta";
import { useStickyHeader } from "../../shared/hooks/StickyHeader";
import { useReportsDropdown, useFiltersDropdown } from "../../services/reports/index";
import { FormDataFilterReports } from "./reports-interface";
import Filters from "./Filters";
import { FormProvider, useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import { CircularProgressCustom } from "../../utils/muiCustomTheme";
import generateDateRange from "../../utils/getMinMaxDate";
import dayjs from "dayjs";
import useFilterLocalStorage from "../../shared/hooks/useFilterLocalStorage";

function Reports() {
  const { headerRef, sentinelRef, isSticky } = useStickyHeader();
  const { getFilterValue, updateFilter, filters } = useFilterLocalStorage('reports');
  const { data: dataFilterDropdown, isPending: isPendingDataFilterDropdown } = useFiltersDropdown(
    {
      query: {
        table_name: "data_dashboard",
      },
    }
  );

  const { data: countries = [], isLoading: isCountriesLoading } = useCountryList();

  const marketOptions = useMemo(() => (isCountriesLoading ?
    [] :
    ["All", ...countries.map((country: any) => country.name)]),
  [isCountriesLoading, countries]);

  const brandOptions = useMemo(() => (isPendingDataFilterDropdown || !dataFilterDropdown?.Brand ?
    [] :
    ["All", ...dataFilterDropdown.Brand.map((brand: any) => brand || "[]")]),
  [isPendingDataFilterDropdown, dataFilterDropdown]);

  const consentTypeOptions = useMemo(() => (isPendingDataFilterDropdown || !dataFilterDropdown?.ConsentType ?
    [] :
    ["All", ...dataFilterDropdown.ConsentType.map((consent: any) => consent || "[]")]),
  [isPendingDataFilterDropdown, dataFilterDropdown]);

  const datePeriodOptions = useMemo(() => (
    ["Last 7 Days", "Last 30 Days", "Last 90 Days",
      "Last Month", "Last Quarter", "Last Year", "MTD", "QTD", "YTD", "Custom"
    ]),
  []);

  const methods = useForm<FormDataFilterReports>({
    mode: "onChange",
    defaultValues: {
      market: [],
      brands: [],
      consentType: [],
      datePeriod: ["Last Year"],
      startDate: null,
      endDate: null,
      minDate: null,
      maxDate: null,
      generateReportClicked: false,
    }
  });

  const { watch, setValue, reset } = methods;

  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) return;
    if (!isPendingDataFilterDropdown && dataFilterDropdown && marketOptions.length > 0 && brandOptions.length > 0) {
      const storedMarket = ('market' in filters) ? getFilterValue('market') : ['All'];
      const storedBrands = ('brands' in filters) ? getFilterValue('brands') : ['All'];
      const storedConsent = ('consentType' in filters) ? getFilterValue('consentType') : ['All'];
      const storedDatePeriod = ('datePeriod' in filters) ? getFilterValue('datePeriod') : ["Last Year"];

      const normalize = (vals: string[], options: string[]) => {
        if (!Array.isArray(vals) || vals.length === 0) return ['All'];
        if (vals.length === 1 && vals[0] === 'All') return options;
        return vals.filter((v) => options.includes(v));
      };

      const nextMarket = normalize(storedMarket, marketOptions);
      const nextBrands = normalize(storedBrands, brandOptions);
      const nextConsent = normalize(storedConsent, consentTypeOptions);
      const dpRaw = Array.isArray(storedDatePeriod) ? storedDatePeriod[0] : storedDatePeriod;
      const nextDatePeriod = datePeriodOptions.includes(dpRaw) ? [dpRaw] : ["Last Year"];

      reset({
        market: nextMarket,
        brands: nextBrands,
        consentType: nextConsent,
        datePeriod: nextDatePeriod,
        startDate: dataFilterDropdown.MaxDate ? dayjs(dataFilterDropdown.MaxDate).subtract(1, 'day') : dayjs().subtract(2, 'day'),
        endDate: dataFilterDropdown.MaxDate ? dayjs(dataFilterDropdown.MaxDate) : dayjs().subtract(1, 'day'),
        minDate: dataFilterDropdown.MinDate ? dayjs(dataFilterDropdown.MinDate) : dayjs().subtract(2, 'day'),
        maxDate: dataFilterDropdown.MaxDate ? dayjs(dataFilterDropdown.MaxDate) : dayjs().subtract(1, 'day'),
        generateReportClicked: false,
      });

      if (!('market' in filters)) {
        updateFilter('market', nextMarket);
      }
      if (!('brands' in filters)) {
        updateFilter('brands', nextBrands);
      }
      if (!('consentType' in filters)) {
        updateFilter('consentType', nextConsent);
      }
      if (!('datePeriod' in filters)) {
        updateFilter('datePeriod', nextDatePeriod);
      }
      // Always persist normalized selections to localStorage after validation
      updateFilter('market', nextMarket);
      updateFilter('brands', nextBrands);
      updateFilter('consentType', nextConsent);
      updateFilter('datePeriod', nextDatePeriod);

      // Build and send initial query once, using normalized selections
      const query: Record<string, any> = { table_name: TABLE_NAME };
      const allCountryNames = marketOptions.filter((c) => c !== 'All');
      const countriesToSend = (Array.isArray(nextMarket) && nextMarket.includes('All'))
        ? allCountryNames
        : nextMarket;
      query.countries = countriesToSend;
      if (nextBrands && !nextBrands.includes('All')) query.brands = nextBrands;
      if (nextConsent && !nextConsent.includes('All')) query.consent_types = nextConsent;
      const datePeriod = Array.isArray(nextDatePeriod) ? nextDatePeriod[0] : nextDatePeriod;
      if (datePeriod === 'Custom') {
        if (dataFilterDropdown.MaxDate) {
          const start = dayjs(dataFilterDropdown.MaxDate).subtract(1, 'day').format('YYYY-MM-DD');
          const end = dayjs(dataFilterDropdown.MaxDate).format('YYYY-MM-DD');
          query.creation_times = [{ start, end }];
        }
      } else {
        const { start, end } = generateDateRange(datePeriod);
        query.creation_times = [{ start, end }];
      }
      setRequestQuery(query);
      initializedRef.current = true;
    }
  }, [isPendingDataFilterDropdown, dataFilterDropdown, marketOptions, brandOptions, consentTypeOptions, reset]);

  const selectedMarket = watch("market");
  const selectedBrand = watch("brands");
  const selectedConsentType = watch("consentType");
  const selectedDatePeriod = watch("datePeriod");
  const generateReportClicked = watch("generateReportClicked");
  const startDate = watch('startDate');
  const endDate = watch('endDate');

  // Zapisy do localStorage odbywają się w Filters przy każdej zmianie poprzez updateFilter

  const handleTabSelect = (tabName: any) => {
    setSelectedTab(tabName);
  };

  const TABLE_NAME = "entity_consentinfo_table";

  const [requestQuery, setRequestQuery] = useState<Record<string, any> | null>(null);
  useEffect(() => {
    if (!generateReportClicked) return;
    const query: Record<string, any> = { table_name: TABLE_NAME };
    const allCountryNames = marketOptions.filter((c) => c !== 'All');
    const countriesToSend = (Array.isArray(selectedMarket) && selectedMarket.includes('All'))
      ? allCountryNames
      : (selectedMarket || []);
    query.countries = countriesToSend;

    if (selectedBrand && !selectedBrand.includes("All")) {
      query.brands = selectedBrand;
    }

    if (selectedConsentType && !selectedConsentType.includes("All")) {
      query.consent_types = selectedConsentType;
    }

    const datePeriod = Array.isArray(selectedDatePeriod) ? selectedDatePeriod[0] : selectedDatePeriod;

    if (datePeriod) {
      if (datePeriod === "Custom") {
        if (startDate && endDate) {
          query.creation_times = [{
            start: startDate.format('YYYY-MM-DD'),
            end: endDate.format('YYYY-MM-DD')
          }];
        }
      } else {
        const { start, end } = generateDateRange(datePeriod);
        query.creation_times = [{ start, end }];
      }
    }
    setRequestQuery(query);
    setValue('generateReportClicked', false);
  }, [generateReportClicked, selectedMarket, selectedBrand, selectedConsentType, selectedDatePeriod, startDate, endDate, setValue]);

  const { data: dataReportsDropdown, isPending: isPendingDataReportsDropdown } = useReportsDropdown({
    query: requestQuery || {},
    options: {
      enabled: !!requestQuery,
    },
  });

  const [selectedTab, setSelectedTab] = useState("Consent Summary");

  const tabs = [
    {
      name: "Consent Summary",
      component: <ConsentSummary dataReportsDropdown={dataReportsDropdown} />,
    },
  ];

  if (isPendingDataReportsDropdown) { // Show loader until form defaults are set
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgressCustom />
      </Box>
    );
  }

  return (
    <FormProvider {...methods}>
      <div className="reports">
        <div ref={sentinelRef}></div>
        <div ref={headerRef} className={`reports__wrap ${isSticky ? "is-sticky" : ""}`}>
          <div className="campaigns__container__btn-wrap">
            <div className="reports__container__title">Reports</div>
          </div>
          <Divider variant="middle" className="header-divider" />
          <div className="reports__nav">
            {tabs.map((tab) => (
              <div
                key={tab.name}
                className={`reports__nav__item ${selectedTab === tab.name ? "selected" : ""}`}
                onClick={() => handleTabSelect(tab.name)}
              >
                {tab.name}
              </div>
            ))}
          </div>
          <div>
            {!isPendingDataFilterDropdown ? (
                <Filters
                marketOptions={marketOptions}
                brandOptions={brandOptions}
                consentTypeOptions={consentTypeOptions}
                datePeriodOptions={datePeriodOptions}
              />
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
                <CircularProgressCustom />
              </Box>
            )}
          </div>
        </div>
        <div className="reports__container">
          <div className="reports__content-wrap">
            {tabs.find((tab) => tab.name === selectedTab)?.component || (
              <div className="reports__content-wrap__placeholder"></div>
            )}
          </div>
        </div>
      </div>
    </FormProvider>
  );
}

export default Reports;
